import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Github,
  ExternalLink,
  Linkedin,
  Twitter,
  Mail,
  MapPin,
  ArrowUpRight,
  Code2,
  Heart,
} from "lucide-react";
import { personalInfo, navLinks } from "../data/mock";
import { SectionReveal } from "./SectionReveal";

const socialLinks = [
  { name: "GitHub", href: personalInfo.links.github, icon: Github, description: "Open source contributions" },
  { name: "WordPress.org", href: personalInfo.links.wordpress, icon: ExternalLink, description: "WordPress profile" },
  { name: "LinkedIn", href: personalInfo.links.linkedin, icon: Linkedin, description: "Professional network" },
  { name: "X / Twitter", href: personalInfo.links.twitter, icon: Twitter, description: "Thoughts & updates" },
  { name: "Email", href: personalInfo.links.email, icon: Mail, description: "Get in touch" },
];

const ContactSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section id="contact" className="relative py-28 lg:py-36 bg-zinc-50 dark:bg-zinc-950 transition-colors duration-500" ref={sectionRef} aria-label="Contact Hit Bhalodia">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
          <SectionReveal animation="clipReveal">
            <div>
              <span className="text-sm font-mono text-emerald-600/80 dark:text-emerald-400/80 tracking-widest uppercase">Contact</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 dark:text-zinc-50 tracking-tight leading-tight">
                Let's build better{" "}<span className="text-emerald-600 dark:text-emerald-400">WordPress</span>{" "}experiences
              </h2>
              <p className="mt-6 text-zinc-600 dark:text-zinc-400 text-lg leading-relaxed max-w-md">
                Interested in collaborating on WordPress, open source, or enterprise engineering? Let's connect.
              </p>
              <div className="mt-8 flex items-center gap-2 text-sm text-zinc-400 dark:text-zinc-600">
                <MapPin className="w-4 h-4" />
                <span className="font-mono">{personalInfo.location}</span>
              </div>
            </div>
          </SectionReveal>

          <div className="space-y-3">
            {socialLinks.map((link, i) => {
              const Icon = link.icon;
              return (
                <motion.a
                  key={link.name}
                  href={link.href}
                  target={link.href.startsWith("mailto") ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: 60, filter: "blur(8px)" }}
                  animate={isInView ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
                  transition={{ duration: 0.6, delay: 0.15 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ x: 4, transition: { duration: 0.2 } }}
                  className="group flex items-center gap-4 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800/50 hover:border-emerald-300 dark:hover:border-emerald-500/20 hover:bg-emerald-50/50 dark:hover:bg-zinc-900/50 transition-colors duration-300 bg-white dark:bg-transparent shadow-sm dark:shadow-none"
                >
                  <div className="w-10 h-10 rounded-lg bg-zinc-100 dark:bg-zinc-800/60 flex items-center justify-center group-hover:bg-emerald-50 dark:group-hover:bg-emerald-500/10 transition-colors">
                    <Icon className="w-5 h-5 text-zinc-500 dark:text-zinc-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors" />
                  </div>
                  <div className="flex-1">
                    <p className="text-zinc-800 dark:text-zinc-200 font-medium text-sm group-hover:text-zinc-950 dark:group-hover:text-white transition-colors">{link.name}</p>
                    <p className="text-zinc-400 dark:text-zinc-600 text-xs">{link.description}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-zinc-300 dark:text-zinc-700 group-hover:text-emerald-500 dark:group-hover:text-emerald-400/60 transition-colors" />
                </motion.a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const scrollTo = (href) => {
    if (href === "#") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="relative bg-white dark:bg-zinc-950 border-t border-zinc-200 dark:border-zinc-800/40 transition-colors duration-500">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <Code2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-zinc-800 dark:text-zinc-200 font-semibold text-sm">{personalInfo.name}</p>
              <p className="text-zinc-400 dark:text-zinc-600 text-xs font-mono">{personalInfo.role} at {personalInfo.company}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            {navLinks.map((link) => (
              <button key={link.href} onClick={() => scrollTo(link.href)} className="text-xs text-zinc-400 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors font-medium">
                {link.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-zinc-100 dark:border-zinc-800/30 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-400 dark:text-zinc-700 font-mono">&copy; {new Date().getFullYear()} {personalInfo.name}. Built with care for the open web.</p>
          <p className="text-xs text-zinc-400 dark:text-zinc-700 flex items-center gap-1">Made with <Heart className="w-3 h-3 text-emerald-500/50" /> and WordPress</p>
        </div>
      </div>
    </footer>
  );
};

export { ContactSection, Footer };
